import Loader from 'components/ui/LoadingIndicator';
import Link from 'components/ui/AppLink';
import ArrowsChevronLeft from '@mds/icons/CaretLeft';
import {
    Box,
    Breadcrumbs,
    Grid,
    Stack,
    Typography,
    Typography as BreadcrumbsText,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import React, { useState } from 'react';
import { MessageView } from 'components/ui/Feedback';
import { ArticleLinkList } from '../../../components/ui/ArticleLinkList';
import Archives from '../../ui/Archives';
import { CategoryItemNode, BrowseArticleStat } from '../../../shared/types';
import { getCategoryItemFromTree } from '../../../utils/list-utils';
import BrowseCategoryList from '../../ui/BrowseCategoryList';
import CategoryCover from '../../ui/CategoryCover';
import { BrowseArticleList } from './BrowseArticleList';
import ArticleTagList from '../../ui/ArticleTagList';
import { useActivityLogger } from '../../hooks/use-activity-logger';
import { useWaitForNumCalls } from '../../hooks/use-wait-for-num-calls';
import { useUser } from '../../providers/UserProvider';
import { CHANGE_SUBSCRIPTION } from '../../../gql/subscriptions';
import ManageTemplateDialog from '../../ui/ManageTemplateDialog';
import { baseUrls } from '../../../utils/baseUrls.utils';
import PremiumChannelList from '../../ui/PremiumChannelList';
import { isValidMongoId } from '../../../utils/id-validation.utils';

import { useMutation, useQuery } from '@apollo/react-hooks';
import { useHistory, useParams } from 'react-router-dom';
import { CATEGORIES_TREE } from '../../../gql/categories';
import { GET_BROWSE_STAT_WITH_SUBSCIPTION } from '../../../gql/articles';
import { DesksSection } from '../../../components/ui/DesksSection';
interface BrowseCategoryPageBreadCrumbsProps {
    browseItem: CategoryItemNode;
    categories: CategoryItemNode[];
    showOnlyFirstParent?: boolean;
    sx?: SxProps<Theme>;
}

const getParentsListById = (
    categories: CategoryItemNode[],
    parentId: string | undefined,
    parentsList: CategoryItemNode[]
) => {
    const categoryFromTree = getCategoryItemFromTree(categories, parentId);

    if (categoryFromTree) {
        parentsList.push(categoryFromTree);
        getParentsListById(categories, categoryFromTree.parent, parentsList);
    }
};

const BrowseCategoryPageBreadCrumbs = ({
    browseItem,
    categories,
    showOnlyFirstParent = false,
    sx,
}: BrowseCategoryPageBreadCrumbsProps): JSX.Element => {
    const parentsList: CategoryItemNode[] = [];
    getParentsListById(categories, browseItem.parent, parentsList);
    parentsList.reverse();

    if (showOnlyFirstParent) {
        const firstParent = parentsList[parentsList.length - 1];
        return (
            <Box component="nav" sx={sx}>
                <Link
                    to={
                        !firstParent
                            ? baseUrls.browse.main
                            : `${baseUrls.browse.main}/${firstParent._id}`
                    }
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        color: 'text.primary',
                    }}
                >
                    <ArrowsChevronLeft fontSize="small" />
                    <Typography variant="body2" component="span">
                        {!firstParent
                            ? 'Back to Browse'
                            : `Back to ${firstParent.name}`}
                    </Typography>
                </Link>
            </Box>
        );
    }

    const breadCrumbsLinks = parentsList.map(
        (parentCategory: CategoryItemNode) => (
            <Link
                to={`${baseUrls.browse.main}/${parentCategory._id}`}
                key={parentCategory._id}
            >
                {parentCategory.name}
            </Link>
        )
    );

    breadCrumbsLinks.unshift(
        <Link to={baseUrls.browse.main} key="all-publications">
            All publications
        </Link>
    );
    breadCrumbsLinks.push(
        <BreadcrumbsText key={browseItem._id}>
            {browseItem.name}
        </BreadcrumbsText>
    );

    if (parentsList.length) {
        return <Breadcrumbs sx={sx}>{breadCrumbsLinks}</Breadcrumbs>;
    }

    return (
        <Breadcrumbs sx={sx}>
            <Link to={baseUrls.browse.main}>All publications</Link>
            <BreadcrumbsText>{browseItem.name}</BreadcrumbsText>
        </Breadcrumbs>
    );
};

const BrowseCategoryPage: React.FC = () => {
    const { push } = useHistory();
    const { categoryId } = useParams<{ categoryId: string }>();
    const isValidCategoryId = isValidMongoId(categoryId);
    const { userDetails } = useUser();
    const [confirmationDialogOpened, setConfirmationDialogOpened] =
        useState<boolean>(false);

    const errorMessage = (
        <MessageView
            styleName="primary"
            size="large"
            title="Category not found"
            primaryAction={{
                label: 'Return to Neon Insights',
                onClick: () => push('/'),
            }}
        />
    );

    const queryCategoryTree = useQuery<{
        categoriesAsTree: CategoryItemNode[];
    }>(CATEGORIES_TREE, {
        skip: !isValidCategoryId,
        fetchPolicy: 'network-only',
    });

    const treeData = queryCategoryTree.data?.categoriesAsTree ?? [];
    const browseItem = getCategoryItemFromTree(treeData, categoryId);

    const pageLoadComplete = useActivityLogger(
        'Browse',
        browseItem?.name,
        categoryId,
        true
    );

    const componentLoaded = useWaitForNumCalls(3, pageLoadComplete);
    const queryStat = useQuery<{
        getBrowseStat: BrowseArticleStat;
        isSubscribedTo?: boolean;
    }>(GET_BROWSE_STAT_WITH_SUBSCIPTION, {
        skip: !isValidCategoryId,
        fetchPolicy: 'network-only',
        variables: { categoryId },
        onCompleted: componentLoaded,
    });

    const [changeSubscription] = useMutation(CHANGE_SUBSCRIPTION, {
        refetchQueries: [
            {
                query: GET_BROWSE_STAT_WITH_SUBSCIPTION,
                variables: { categoryId },
            },
        ],
        awaitRefetchQueries: true,
    });

    if (!isValidCategoryId) {
        return errorMessage;
    }

    if (queryCategoryTree.loading || queryStat.loading || !queryStat.data)
        return <Loader />;

    if (!browseItem) {
        return errorMessage;
    }
    const isSubscribedToCategory: boolean =
        queryStat?.data?.isSubscribedTo ?? false;

    const desksToShow = queryStat.data.getBrowseStat?.desks;
    const tagsToShow = queryStat.data.getBrowseStat?.tags;
    const trendingArticles = queryStat.data.getBrowseStat?.trendingArticles;

    function modifySubscription() {
        if (!isSubscribedToCategory) {
            doModifySubscription();
        } else {
            setConfirmationDialogOpened(true);
        }
    }

    function doModifySubscription() {
        changeSubscription({
            variables: {
                item: {
                    userId: userDetails?.id,
                    entityId: categoryId,
                    entityType: 'Category',
                    name: browseItem?.name,
                    isAdd: !isSubscribedToCategory,
                },
            },
        });
    }

    return (
        <Grid
            container
            spacing={2}
            sx={(theme) => ({ [theme.breakpoints.only('md')]: { mt: 1 } })}
        >
            <Grid size={12}>
                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                    <BrowseCategoryPageBreadCrumbs
                        categories={treeData}
                        browseItem={browseItem}
                        sx={{ mt: -2 }}
                    />
                </Box>
                <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                    <BrowseCategoryPageBreadCrumbs
                        categories={treeData}
                        browseItem={browseItem}
                        showOnlyFirstParent
                        sx={{ pb: 2 }}
                    />
                </Box>
                <CategoryCover
                    name={browseItem.name}
                    id={browseItem._id}
                    description={browseItem.description}
                    imageUrl={browseItem.largeImage}
                    isSubscribed={isSubscribedToCategory}
                    modifySubscription={modifySubscription}
                    categories={browseItem.children.map((category) => ({
                        id: category._id,
                        name: category.name,
                        url: `${baseUrls.browse.main}/${category._id}`,
                    }))}
                />
            </Grid>
            <Grid
                size={{
                    xs: 12,
                    md: 8,
                }}
            >
                <BrowseArticleList
                    title="Latest articles"
                    categoryId={categoryId}
                    showPlacement={true}
                    onDataLoaded={componentLoaded}
                />
                <ManageTemplateDialog
                    title="Are you sure you want to unfollow?"
                    dialogMessage={
                        <>
                            You are about to unfollow {browseItem?.name}.
                            <br></br>
                            <br></br>
                            If you unfollow you will no longer see related
                            articles on your For You section, and you won’t
                            receive email notifications in your inbox.
                            <br></br>
                            <br></br>
                            If you prefer, you can{' '}
                            <Link to={baseUrls.emailNotifications}>
                                manage email notifications
                            </Link>
                            .
                        </>
                    }
                    submitBtnText="Yes, unfollow"
                    cancelBtnText="Cancel"
                    dialogOpenState={confirmationDialogOpened}
                    onDelete={() => {
                        doModifySubscription();
                        setConfirmationDialogOpened(false);
                    }}
                    submitBtnColorType="primary"
                    closeDialog={() => setConfirmationDialogOpened(false)}
                />
            </Grid>
            <Grid
                sx={{ mt: { xs: 4, md: 0 }, pl: { md: 5, lg: 9 } }}
                size={{
                    xs: 12,
                    md: 4,
                }}
            >
                <Stack spacing={6}>
                    <BrowseCategoryList
                        categories={browseItem.children}
                        parentCategoryName={browseItem.name}
                        onDataLoaded={componentLoaded}
                    />
                    <ArticleLinkList
                        title={`Trending in ${browseItem.name}`}
                        articles={trendingArticles}
                    />
                    <PremiumChannelList maxLength={25} />
                    <ArticleTagList showTwoRowsOnly tags={tagsToShow} />
                    <DesksSection deskList={desksToShow} />
                    <Archives />
                </Stack>
            </Grid>
        </Grid>
    );
};

export default BrowseCategoryPage;
